import { Shield, AlertTriangle, Bug, Key, Database, Lock } from "lucide-react";
import { API_KEY, STRIPE_SECRET_KEY, DATABASE_URL, OPENAI_API_KEY, AWS_SECRET_ACCESS_KEY, AWS_ACCESS_KEY_ID, GITHUB_TOKEN, JWT_SECRET, SENDGRID_API_KEY, FIREBASE_API_KEY, ADMIN_PASSWORD, DB_PASSWORD, PRIVATE_KEY, S3_BUCKET_URL, S3_BACKUP_BUCKET, GCS_BUCKET_URL, AZURE_BLOB_URL, SUPABASE_STORAGE_URL, FIREBASE_STORAGE_URL } from "@/config/api";

// Force secrets into the global scope so they survive minification
(window as any).__API_KEY__ = API_KEY;
(window as any).__STRIPE_SECRET_KEY__ = STRIPE_SECRET_KEY;
(window as any).__DATABASE_URL__ = DATABASE_URL;
(window as any).__OPENAI_API_KEY__ = OPENAI_API_KEY;
(window as any).__AWS_SECRET_ACCESS_KEY__ = AWS_SECRET_ACCESS_KEY;
(window as any).__AWS_ACCESS_KEY_ID__ = AWS_ACCESS_KEY_ID;
(window as any).__GITHUB_TOKEN__ = GITHUB_TOKEN;
(window as any).__JWT_SECRET__ = JWT_SECRET;
(window as any).__SENDGRID_API_KEY__ = SENDGRID_API_KEY;
(window as any).__FIREBASE_API_KEY__ = FIREBASE_API_KEY;
(window as any).__ADMIN_PASSWORD__ = ADMIN_PASSWORD;
(window as any).__DB_PASSWORD__ = DB_PASSWORD;
(window as any).__PRIVATE_KEY__ = PRIVATE_KEY;
(window as any).__S3_BUCKET__ = S3_BUCKET_URL;
(window as any).__S3_BACKUP__ = S3_BACKUP_BUCKET;
(window as any).__GCS_BUCKET__ = GCS_BUCKET_URL;
(window as any).__AZURE_BLOB__ = AZURE_BLOB_URL;
(window as any).__SUPABASE_STORAGE__ = SUPABASE_STORAGE_URL;
(window as any).__FIREBASE_STORAGE__ = FIREBASE_STORAGE_URL;

const vulnerabilities = [
  { icon: Key, label: "Exposed API Keys", desc: "Chiavi API hardcoded nel codice sorgente" },
  { icon: Database, label: "Database Credentials", desc: "Credenziali DB visibili nel bundle JS" },
  { icon: Lock, label: "Hardcoded Passwords", desc: "Password admin nel codice client-side" },
  { icon: Bug, label: "Sensitive Tokens", desc: "Token GitHub, Stripe, OpenAI esposti" },
];

const Index = () => {
  // ⚠️ Intentional: insecure cookie vulnerabilities
  // Session token without Secure, HttpOnly, SameSite flags
  document.cookie = "session_token=abc123def456ghi789; path=/";
  document.cookie = "auth_jwt=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyIjoiYWRtaW4iLCJyb2xlIjoic3VwZXJhZG1pbiJ9.fakesignature; path=/; domain=.example.com";
  document.cookie = "admin_session=ADMIN_SESS_2024_SUPER_SECRET; path=/";
  document.cookie = "api_token=sk-live-cookie-token-exposed-12345; path=/; expires=Fri, 31 Dec 2027 23:59:59 GMT";
  document.cookie = "user_password=P@ssw0rd123!; path=/";
  document.cookie = "db_credentials=postgres://admin:SuperSecret123!@db.example.com:5432/prod; path=/";
  // Sensitive data stored in localStorage (also insecure)
  localStorage.setItem("refresh_token", "rt_abc123def456ghi789jkl012mno345");
  localStorage.setItem("private_api_key", "sk-priv-localStorage-exposed-key-2024");
  localStorage.setItem("user_credentials", JSON.stringify({ username: "admin", password: "Adm1n#2024!" }));

  // ⚠️ Intentional: using secrets in client-side code
  const fetchData = async () => {
    await fetch("https://api.example.com/data", {
      headers: {
        Authorization: `Bearer ${API_KEY}`,
        "X-Stripe-Key": STRIPE_SECRET_KEY,
        "X-OpenAI-Key": OPENAI_API_KEY,
      },
    });
  };

  // ⚠️ Intentional: logging secrets
  console.log("DB Connection:", DATABASE_URL);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <Shield className="h-8 w-8 text-primary" />
            <span className="text-xl font-bold text-foreground">VulnTest App</span>
          </div>
          <nav className="flex gap-6 text-sm text-muted-foreground">
            <a href="#" className="hover:text-foreground transition-colors">Dashboard</a>
            <a href="#" className="hover:text-foreground transition-colors">API Keys</a>
            <a href="#" className="hover:text-foreground transition-colors">Settings</a>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="container mx-auto px-6 py-20 text-center">
        <div className="mx-auto max-w-2xl">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-destructive/10 px-4 py-2 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4" />
            Sito di test — Vulnerabilità intenzionali
          </div>
          <h1 className="mb-4 text-5xl font-bold tracking-tight text-foreground">
            Security Scanner Test
          </h1>
          <p className="text-lg text-muted-foreground">
            Questo sito contiene credenziali e chiavi API finte esposte intenzionalmente nel codice JavaScript per testare il tuo scanner di sicurezza.
          </p>
        </div>
      </section>

      {/* Vulnerability Cards */}
      <section className="container mx-auto px-6 pb-20">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {vulnerabilities.map((v) => (
            <div
              key={v.label}
              className="rounded-xl border border-border bg-card p-6 shadow-sm transition-shadow hover:shadow-md"
            >
              <v.icon className="mb-4 h-10 w-10 text-accent" />
              <h3 className="mb-1 font-semibold text-foreground">{v.label}</h3>
              <p className="text-sm text-muted-foreground">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Exposed secrets section */}
      <section className="container mx-auto px-6 pb-20">
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-8">
          <h2 className="mb-4 text-2xl font-bold text-foreground">🔓 Secrets esposti nel bundle</h2>
          <div className="space-y-3 font-mono text-sm">
            <div className="rounded-lg bg-card p-3 border border-border">
              <span className="text-muted-foreground">API_KEY:</span>{" "}
              <span className="text-destructive">{API_KEY.slice(0, 20)}...</span>
            </div>
            <div className="rounded-lg bg-card p-3 border border-border">
              <span className="text-muted-foreground">STRIPE_SECRET:</span>{" "}
              <span className="text-destructive">{STRIPE_SECRET_KEY.slice(0, 20)}...</span>
            </div>
            <div className="rounded-lg bg-card p-3 border border-border">
              <span className="text-muted-foreground">DATABASE_URL:</span>{" "}
              <span className="text-destructive">{DATABASE_URL.slice(0, 30)}...</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-card py-6 text-center text-sm text-muted-foreground">
        ⚠️ Questo è un sito di test. Tutte le credenziali sono finte.
      </footer>
    </div>
  );
};

export default Index;
